import { useContext } from 'react'

import { useForm, useLatestEntity, useThunkDispatch } from '@domain-group/react-hooks'
import type { UseFormOptions, UseFormPayload } from '@domain-group/react-hooks'

import useSelector from '@hooks/useSelector'

import PageContext from '@contexts/pageContext'

import * as geoSuburbActions from '@redux/modules/geoSuburb'
import type { AppDispatch } from '@redux/store'
import type { ActionResult, EntityOptions } from '@redux/modules/module'

import defaultState, { requiredFields } from '@models/geoSuburb'
import type { GeoStateModel, GeoSuburbModel } from '@models/types'

export const generateUrls = (geoSuburb: Partial<GeoSuburbModel> = {}) => {
  const { id } = geoSuburb

  return {
    editGeoSuburbUrl: `#/systemManager/geoSuburbs/${id}/edit`,
    geoSuburbsIndexUrl: '#/systemManager/geoSuburbs?active=true',
    inactiveGeoSuburbsIndexUrl: '#/systemManager/geoSuburbs?active=false',
  }
}

export type CreateGeoSuburbParams = {
  geoSuburbParams: GeoSuburbModel,
  dispatch: AppDispatch,
  requestOptions?: EntityOptions,
}

const createGeoSuburb = (params: CreateGeoSuburbParams) => {
  const { geoSuburbParams, dispatch, requestOptions } = params
  const { createGeoSuburb: createFn } = geoSuburbActions

  return dispatch(createFn(geoSuburbParams, requestOptions))
}

type UpdateGeoSuburbPayload = {
  geoSuburbParams: GeoSuburbModel,
  dispatch: AppDispatch,
  requestOptions?: EntityOptions,
}

const updateGeoSuburb = (params: UpdateGeoSuburbPayload) => {
  const { geoSuburbParams, dispatch, requestOptions } = params
  const { updateGeoSuburb: updateFn } = geoSuburbActions

  return dispatch(updateFn(geoSuburbParams, requestOptions))
}

type LaunchCreateOrEditSuburbModalParams = {
  geoSuburb: GeoSuburbModel,
  showCreateOrEditSuburbModal: Function,
}

const createOrEditSuburb = (params: LaunchCreateOrEditSuburbModalParams) => (
  new Promise((resolve, reject) => {
    const { geoSuburb, showCreateOrEditSuburbModal } = params

    if (showCreateOrEditSuburbModal){
      const payload = {
        geoSuburb,
      }

      showCreateOrEditSuburbModal(payload)

      resolve({ success: true, result: {} })
    }

    reject(new Error('showCreateOrEditSuburbModal not defined in PageContext callbacks'))
  })
)

export function useGeoSuburbForm(
  geoSuburb: Partial<GeoSuburbModel> = {},
  options: UseFormOptions = {},
): UseFormPayload {
  const { validateOn } = options

  const geoSuburbForm = useForm(
    defaultState,
    { entity: geoSuburb, requiredFields, validateOn },
    [geoSuburb.id, geoSuburb.cache_key],
  )

  return {
    ...geoSuburbForm,
  }
}

export function useRelations(geoSuburb: Partial<GeoSuburbModel>) {
  const { geo_state_id } = geoSuburb || {}

  const geoStates = useSelector(reduxState => reduxState.entities.geoStates)

  const geoState: Partial<GeoStateModel> = geo_state_id ? geoStates[geo_state_id] || {} : {}

  return {
    geoState,
  }
}

export type UseGeoSuburbPayload = {
  callbacks: {
    createGeoSuburb: (geoSuburbParams: GeoSuburbModel, requestOptions: EntityOptions) => (
      Promise<ActionResult<GeoSuburbModel>>
    ),
    createOrEditSuburb: () => Promise<unknown>,
    updateGeoSuburb: (geoSuburbParams: GeoSuburbModel, requestOptions: EntityOptions) => (
      Promise<ActionResult<GeoSuburbModel>>
    ),
  },
  creating: boolean,
  geoState: GeoStateModel | {},
  geoSuburb: GeoSuburbModel,
  loading: boolean,
  updating: boolean,
  urls: ReturnType<typeof generateUrls>,
}

function useGeoSuburb(initEntity: Partial<GeoSuburbModel> = {}): UseGeoSuburbPayload {
  const { entity: geoSuburb } = useLatestEntity(initEntity, 'geoSuburbs')

  const dispatch = useThunkDispatch()

  const { callbacks } = useContext(PageContext)
  const { showCreateOrEditSuburbModal } = callbacks || {}

  const { creating, loading, updating } = useSelector(reduxState => reduxState.geoSuburbs)

  const { geoState } = useRelations(geoSuburb)

  return {
    callbacks: {
      createGeoSuburb: (geoSuburbParams: GeoSuburbModel, requestOptions: EntityOptions) => (
        createGeoSuburb({ geoSuburbParams, dispatch, requestOptions })
      ),
      // @ts-expect-error TS2322
      createOrEditSuburb: () => createOrEditSuburb({ geoSuburb, showCreateOrEditSuburbModal }),
      updateGeoSuburb: (geoSuburbParams: GeoSuburbModel, requestOptions: EntityOptions) => (
        updateGeoSuburb({ geoSuburbParams, dispatch, requestOptions })
      ),
    },
    creating,
    geoState,
    // @ts-expect-error TS2322
    geoSuburb,
    loading,
    updating,
    urls: generateUrls(geoSuburb),
  }
}

export default useGeoSuburb
